import { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Menu, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@slumbr/ui";

import { useIsMobile } from "@/hooks/use-is-mobile";
import { MobileHeader } from "@/shell/mobile-header";
import { WorkspaceTree } from "./workspace-tree";
import { TrashPanel } from "./trash-panel";
import { TemplatePicker } from "./template-picker";
import { useNotesStore } from "../stores/notes-store";
import type { NoteTemplate } from "../utils/note-templates";

const NOTES_REVALIDATE_MS = 5 * 60_000;

export function NotesLayout() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const setActiveNote = useNotesStore((s) => s.setActiveNote);
  const createNoteRemote = useNotesStore((s) => s.createNoteRemote);
  const updateNoteRemote = useNotesStore((s) => s.updateNoteRemote);
  const isLoading = useNotesStore((s) => s.isLoading);
  const error = useNotesStore((s) => s.error);
  const fetchNotes = useNotesStore((s) => s.fetchNotes);

  // Cached notes render immediately, but the list must always revalidate so
  // notes created on another device are merged into this device's sidebar.
  useEffect(() => {
    let lastFetchAt = 0;

    const refresh = () => {
      const now = Date.now();
      if (now - lastFetchAt < 1500) return;
      lastFetchAt = now;
      fetchNotes().catch(() => {
        // Error is already captured in the store
      });
    };

    refresh();

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, NOTES_REVALIDATE_MS);
    const handleFocus = () => refresh();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchNotes]);

  const handleSelectNote = (noteId: string) => {
    setActiveNote(noteId);
    navigate(`/notes/${noteId}`);
    if (isMobile) setDrawerOpen(false);
  };

  const handleNewNote = () => {
    setTemplateOpen(true);
  };

  const handlePickTemplate = (template: NoteTemplate) => {
    setTemplateOpen(false);
    void createNoteRemote({ title: template.title, parentId: null }).then(
      async (newNoteId) => {
        // Seed the body for non-blank templates so the note opens pre-filled.
        if (template.id !== "blank") {
          try {
            await updateNoteRemote(newNoteId, { content: template.build() });
          } catch {
            // Seeding is best-effort; the empty note still opens.
          }
        }
        setActiveNote(newNoteId);
        navigate(`/notes/${newNoteId}`);
        if (isMobile) setDrawerOpen(false);
      }
    );
  };

  const SidebarSkeleton = () => (
    <div className="flex h-full flex-col p-3">
      <div className="mb-3 h-9 w-full animate-pulse rounded-2xl bg-white/[0.04]" />
      <div className="mb-2 h-6 w-24 animate-pulse rounded-full bg-white/[0.04]" />
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="mb-1.5 h-7 w-full animate-pulse rounded-xl bg-white/[0.04]" />
      ))}
    </div>
  );

  const SidebarError = () => (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <AlertCircle className="h-8 w-8 text-red-300" />
      <p className="text-sm text-red-300">{error || "Failed to load notes"}</p>
      <button
        onClick={() => fetchNotes()}
        className="rounded-full bg-[#c83050] px-4 py-2 text-sm font-black text-white transition hover:bg-[#d63b5d]"
      >
        Retry
      </button>
    </div>
  );

  const sidebarContent = isLoading ? (
    <SidebarSkeleton />
  ) : error ? (
    <SidebarError />
  ) : (
    <WorkspaceTree onSelectNote={handleSelectNote} onNewNote={handleNewNote} onOpenTrash={() => setTrashOpen(true)} />
  );

  if (isMobile) {
    return (
      <>
        <div className="flex h-full flex-col">
          {/* Mobile header with hamburger */}
          <MobileHeader
            title="Notes"
            leftAction={
              <button
                onClick={() => setDrawerOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                aria-label="Open notes sidebar"
              >
                <Menu className="h-5 w-5" />
              </button>
            }
          />

          {/* Drawer overlay */}
          <div
            className={cn(
              "fixed inset-0 z-40 bg-black/50 transition-opacity duration-200",
              drawerOpen ? "opacity-100" : "pointer-events-none opacity-0"
            )}
            onClick={() => setDrawerOpen(false)}
          />

          <div
            className={cn(
              "fixed left-0 top-0 z-50 h-full w-72 border-r border-white/[0.06] bg-[#0b0d0f] shadow-2xl shadow-black/60 transition-transform duration-200",
              drawerOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            {sidebarContent}
          </div>

          <div className="flex-1 overflow-auto bg-[#0b0d0f] text-[#e7e9ea]">
            <Outlet />
          </div>
        </div>
        <TrashPanel isOpen={trashOpen} onClose={() => setTrashOpen(false)} />
        {templateOpen && (
          <TemplatePicker
            onPick={handlePickTemplate}
            onClose={() => setTemplateOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex h-full bg-[#0b0d0f] text-[#e7e9ea]">
        <div className="w-64 shrink-0 border-r border-white/[0.06]">{sidebarContent}</div>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
      <TrashPanel isOpen={trashOpen} onClose={() => setTrashOpen(false)} />
      {templateOpen && (
        <TemplatePicker
          onPick={handlePickTemplate}
          onClose={() => setTemplateOpen(false)}
        />
      )}
    </>
  );
}
